import { useCallback, useRef, useEffect } from 'react';
import { supabase } from '../integrations/supabase/client';
import { useAuth } from './useAuth';
import { useClubContext } from '../contexts/ClubContext';
import { getFeatureFromRoute } from '../utils/crmAnalyticsMapping';

// Store active page view for tracking exit
let activePageView: {
  id: string;
  enteredAt: Date;
  pagePath: string;
} | null = null;

// Store current session
let currentSessionId: string | null = null;

/**
 * Core hook for tracking CRM usage analytics
 * Handles page views, actions, and session management for club admins
 * Now tracks ACTUAL club admin behavior in the main golf app
 */
export function useCrmTracking() {
  const { user } = useAuth();
  const { currentClub } = useClubContext();
  const sessionStartedRef = useRef(false);

  // Initialize session on first use
  useEffect(() => {
    if (user && currentClub && !sessionStartedRef.current) {
      const existingSession = localStorage.getItem('crm_session_id');
      if (!existingSession || !isValidSession(existingSession)) {
        startSession();
      } else {
        // Parse the JSON to get just the session ID
        try {
          const sessionData = JSON.parse(existingSession);
          currentSessionId = sessionData.id;
        } catch (error) {
          console.error('CRM Tracking: Invalid session data, starting new session');
          startSession();
        }
      }
      sessionStartedRef.current = true;
    }
  }, [user, currentClub]);

  // Cleanup on unmount
  useEffect(() => {
    const handleBeforeUnload = () => {
      if (activePageView) {
        updatePageViewExit(activePageView.id, activePageView.enteredAt);
      }
      if (currentSessionId) {
        endSession(currentSessionId);
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, []);

  /**
   * Track page view - called when user navigates to a new page
   */
  const trackPageView = useCallback(async (pagePath: string) => {
    if (!user || !currentClub) {
      console.warn('CRM Tracking: User or club not available for page view tracking', {
        user: !!user,
        currentClub: !!currentClub
      });
      return;
    }

    try {
      // End previous page view if exists
      if (activePageView) {
        await updatePageViewExit(activePageView.id, activePageView.enteredAt);
      }

      // Get feature mapping
      const featureMapping = getFeatureFromRoute(pagePath);
      if (!featureMapping) {
        console.warn(`CRM Tracking: No feature mapping found for route: ${pagePath}`);
        return;
      }

      // Ensure we have a session
      if (!currentSessionId) {
        currentSessionId = await startSession();
      }

      // Detect device and browser
      const deviceType = getDeviceType();
      const browser = getBrowser();
      const screenResolution = `${window.screen.width}x${window.screen.height}`;

      // Insert page view record
      const { data, error } = await supabase
        .from('crm_page_views')
        .insert({
          club_id: currentClub.id,
          user_id: user.id,
          page_path: pagePath,
          feature_name: featureMapping.featureName,
          entered_at: new Date().toISOString(),
          device_type: deviceType,
          browser: browser,
          screen_resolution: screenResolution,
          referrer_page: activePageView?.pagePath || null,
          session_id: currentSessionId
        })
        .select()
        .single();

      if (error) {
        console.error('CRM Tracking: Failed to track page view:', error);
        return;
      }

      // Store for exit tracking
      activePageView = {
        id: data.id,
        enteredAt: new Date(),
        pagePath: pagePath
      };

      console.log(`CRM Tracking: Page view tracked - ${featureMapping.featureName} (Club: ${currentClub.name})`);

    } catch (error) {
      console.error('CRM Tracking: Error in trackPageView:', error);
    }
  }, [user, currentClub]);

  /**
   * Track admin action - called when user performs an action
   */
  const trackAction = useCallback(async (
    actionType: string,
    success: boolean = true,
    metadata: any = {},
    errorMessage?: string
  ) => {
    if (!user || !currentClub || !currentSessionId) {
      console.warn('CRM Tracking: Missing context for action tracking', {
        user: !!user,
        currentClub: !!currentClub,
        currentSessionId: !!currentSessionId
      });
      return;
    }

    try {
      // Get current feature from active page view
      const featureMapping = activePageView ?
        getFeatureFromRoute(activePageView.pagePath) : null;

      if (!featureMapping) {
        console.warn('CRM Tracking: No feature context for action tracking');
        return;
      }

      // Insert action record
      const { error } = await supabase
        .from('crm_actions')
        .insert({
          club_id: currentClub.id,
          user_id: user.id,
          action_type: actionType,
          feature_name: featureMapping.featureName,
          page_path: activePageView?.pagePath,
          success: success,
          error_message: errorMessage || null,
          metadata: metadata,
          session_id: currentSessionId
        });

      if (error) {
        console.error('CRM Tracking: Failed to track action:', error);
        return;
      }

      console.log(`CRM Tracking: Action tracked - ${actionType} (${success ? 'success' : 'failed'}) (Club: ${currentClub.name})`);

    } catch (error) {
      console.error('CRM Tracking: Error in trackAction:', error);
    }
  }, [user, currentClub]);

  /**
   * Start a new session
   */
  const startSession = useCallback(async (): Promise<string> => {
    if (!user || !currentClub) {
      throw new Error('CRM Tracking: Cannot start session without user and club');
    }

    try {
      const sessionId = crypto.randomUUID();
      const deviceType = getDeviceType();
      const browser = getBrowser();
      const ipAddress = await getClientIP();

      // Insert session record
      const { error } = await supabase
        .from('crm_sessions')
        .insert({
          id: sessionId,
          club_id: currentClub.id,
          user_id: user.id,
          started_at: new Date().toISOString(),
          device_type: deviceType,
          browser: browser,
          ip_address: ipAddress,
          is_mobile: deviceType === 'mobile'
        });

      if (error) {
        console.error('CRM Tracking: Failed to start session:', error);
        throw error;
      }

      // Store in localStorage with expiration
      const sessionData = {
        id: sessionId,
        startedAt: Date.now(),
        clubId: currentClub.id
      };
      localStorage.setItem('crm_session_id', JSON.stringify(sessionData));

      currentSessionId = sessionId;
      console.log(`CRM Tracking: Session started for club admin - ${currentClub.name}:`, sessionId);

      return sessionId;

    } catch (error) {
      console.error('CRM Tracking: Error starting session:', error);
      throw error;
    }
  }, [user, currentClub]);

  /**
   * End the current session
   */
  const endSession = useCallback(async (sessionId: string) => {
    if (!sessionId) return;

    try {
      // Get session stats
      const { data: pageViews } = await supabase
        .from('crm_page_views')
        .select('id, feature_name')
        .eq('session_id', sessionId);

      const { data: actions } = await supabase
        .from('crm_actions')
        .select('id')
        .eq('session_id', sessionId);

      const uniqueFeatures = new Set(pageViews?.map(pv => pv.feature_name) || []);

      // Update session record
      const { error } = await supabase
        .from('crm_sessions')
        .update({
          ended_at: new Date().toISOString(),
          duration_minutes: calculateSessionDuration(sessionId),
          pages_visited: pageViews?.length || 0,
          actions_taken: actions?.length || 0,
          unique_features_used: uniqueFeatures.size
        })
        .eq('id', sessionId);

      if (error) {
        console.error('CRM Tracking: Failed to end session:', error);
      } else {
        console.log('CRM Tracking: Club admin session ended:', sessionId);
      }

      // Clear localStorage
      localStorage.removeItem('crm_session_id');
      currentSessionId = null;

    } catch (error) {
      console.error('CRM Tracking: Error ending session:', error);
    }
  }, []);

  return {
    trackPageView,
    trackAction,
    startSession,
    endSession,
    getCurrentSessionId: () => currentSessionId
  };
}

/**
 * Update page view with exit timestamp and duration
 */
async function updatePageViewExit(pageViewId: string, enteredAt: Date) {
  try {
    const exitedAt = new Date();
    const durationSeconds = Math.floor((exitedAt.getTime() - enteredAt.getTime()) / 1000);
    const isBounce = durationSeconds < 10;

    await supabase
      .from('crm_page_views')
      .update({
        exited_at: exitedAt.toISOString(),
        duration_seconds: durationSeconds,
        is_bounce: isBounce
      })
      .eq('id', pageViewId);

    console.log(`CRM Tracking: Page view updated - ${durationSeconds}s (bounce: ${isBounce})`);

  } catch (error) {
    console.error('CRM Tracking: Error updating page view exit:', error);
  }
}

/**
 * Check if session is still valid (not expired)
 */
function isValidSession(sessionJson: string): boolean {
  try {
    const session = JSON.parse(sessionJson);
    const sessionAge = Date.now() - session.startedAt;
    const maxAge = 30 * 60 * 1000; // 30 minutes

    return sessionAge < maxAge;
  } catch {
    return false;
  }
}

/**
 * Calculate session duration in minutes
 */
function calculateSessionDuration(sessionId: string): number {
  try {
    const sessionData = localStorage.getItem('crm_session_id');
    if (!sessionData) return 0;

    const session = JSON.parse(sessionData);
    const durationMs = Date.now() - session.startedAt;
    return Math.floor(durationMs / (1000 * 60));
  } catch {
    return 0;
  }
}

/**
 * Detect device type
 */
function getDeviceType(): string {
  const width = window.innerWidth;
  if (width < 768) return 'mobile';
  if (width < 1024) return 'tablet';
  return 'desktop';
}

/**
 * Detect browser
 */
function getBrowser(): string {
  const userAgent = navigator.userAgent.toLowerCase();
  if (userAgent.includes('chrome')) return 'chrome';
  if (userAgent.includes('firefox')) return 'firefox';
  if (userAgent.includes('safari')) return 'safari';
  if (userAgent.includes('edge')) return 'edge';
  return 'other';
}

/**
 * Get client IP address (simplified)
 */
async function getClientIP(): Promise<string | null> {
  try {
    // This is a simple approach - in production you might want to use a proper IP detection service
    return '0.0.0.0'; // Placeholder - could integrate with ipify.org or similar
  } catch {
    return null;
  }
}